import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { invoicesAPI } from '../api/client';
import { Archive, RotateCcw, Trash2, ArrowLeft, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import CurrencyDisplay, { formatCurrencyAmount } from '../components/CurrencyDisplay';
import TablePagination from '../components/TablePagination';

const DEL_PAGE_SIZE = 25;

function SortTh({ label, sortKey, currentKey, currentDir, onSort, align = 'left' }) {
  const active = currentKey === sortKey;
  const Icon = active ? (currentDir === 'ASC' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className={`table-th cursor-pointer hover:bg-gray-100 select-none ${align === 'right' ? 'text-right' : ''}`} onClick={() => onSort(sortKey)}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}<Icon className={`w-3.5 h-3.5 ${active ? 'text-gray-700' : 'text-gray-400'}`} />
      </span>
    </th>
  );
}

export default function DeletedInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('DeletedAt');
  const [sortDir, setSortDir] = useState('DESC');
  const [page, setPage] = useState(1);

  const fetchDeleted = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getDeleted();
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deleted invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeleted();
  }, []);

  const handleRestore = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Restore invoice ${invoiceNumber}?`)) return;
    
    setRestoring(invoiceId);
    try {
      await invoicesAPI.restore(invoiceId);
      alert(`Invoice ${invoiceNumber} restored successfully`);
      fetchDeleted();
    } catch (error) {
      console.error('Restore error:', error);
      alert(error.response?.data?.message || 'Failed to restore invoice');
    } finally {
      setRestoring(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center py-12">Loading deleted invoices...</div>;
  }

  const filteredInvoices = invoices.filter(inv => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (inv.InvoiceNumber && inv.InvoiceNumber.toLowerCase().includes(t)) ||
           (inv.VendorName && inv.VendorName.toLowerCase().includes(t)) ||
           (inv.DepartmentName && inv.DepartmentName.toLowerCase().includes(t));
  });
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'string' ? av.localeCompare(bv, undefined, {sensitivity:'base'}) : (av - bv);
    return sortDir === 'ASC' ? cmp : -cmp;
  });
  const pagedInvoices = sortedInvoices.slice((page - 1) * DEL_PAGE_SIZE, page * DEL_PAGE_SIZE);
  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortKey(key); setSortDir('ASC'); }
    setPage(1);
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/invoices" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Archive className="w-6 h-6 text-gray-400" />
                Deleted Invoices
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {invoices.length} archived invoice{invoices.length !== 1 ? 's' : ''} — these can be restored
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder="Search invoice, vendor, department…"
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Table */}
        {filteredInvoices.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Trash2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-500">No deleted invoices</h3>
            <p className="text-sm text-gray-400 mt-1">Archived invoices will appear here</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortTh label="Invoice #" sortKey="InvoiceNumber" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Vendor" sortKey="VendorName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Department" sortKey="DepartmentName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Date" sortKey="InvoiceDate" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Amount" sortKey="TotalAmount" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                  <SortTh label="Status" sortKey="Status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Deleted" sortKey="DeletedAt" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Deleted By" sortKey="DeletedByName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <th className="table-th text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedInvoices.map((invoice) => (
                  <tr key={invoice.InvoiceID} className="hover:bg-gray-50">
                    <td className="table-td font-medium text-gray-900">
                      {invoice.InvoiceNumber || '-'}
                    </td>
                    <td className="table-td text-gray-600">
                      {invoice.VendorName || '-'}
                    </td>
                    <td className="table-td text-gray-600">
                      {invoice.DepartmentName || '-'}
                    </td>
                    <td className="table-td text-gray-600">
                      {invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString('en-GB') : '-'}
                    </td>
                    <td className="table-td text-right font-medium">
                      <CurrencyDisplay amount={invoice.TotalAmount} currency={invoice.Currency} />
                    </td>
                    <td className="table-td">
                      <span className={`badge ${getStatusBadgeClass(invoice.Status)}`}>
                        {invoice.Status}
                      </span>
                    </td>
                    <td className="table-td text-gray-500 text-sm">
                      {formatDate(invoice.DeletedAt)}
                    </td>
                    <td className="table-td text-gray-600">
                      {invoice.DeletedByName || '-'}
                    </td>
                    <td className="table-td text-center">
                      <button
                        onClick={() => handleRestore(invoice.InvoiceID, invoice.InvoiceNumber)}
                        disabled={restoring === invoice.InvoiceID}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                        title="Restore invoice"
                      >
                        <RotateCcw className={`w-4 h-4 ${restoring === invoice.InvoiceID ? 'animate-spin' : ''}`} />
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>            <TablePagination page={page} pageSize={DEL_PAGE_SIZE} total={sortedInvoices.length} onPageChange={setPage} />          </div>
        )}
      </div>
    </div>
  );
}

function getStatusBadgeClass(status) {
  const classes = {
    Draft: 'badge-gray',
    Pending: 'badge-warning',
    Approved: 'badge-success',
    Rejected: 'badge-danger',
    Posted: 'badge-info',
  };
  return classes[status] || 'badge-gray';
}
